import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { LangChangeEvent } from '@ngx-translate/core';
import _cloneDeep from 'lodash/cloneDeep';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  map,
  startWith,
  switchMap,
  takeUntil,
  throwError,
} from 'rxjs';
import { FormioCustomComponent, FormioEvent } from 'dp-formio';
import { VisitInfoModel } from '../../../shared/model';
import { removeEmpty } from '../../../shared/utility';
import { ComponentBase } from './base';
import { UsersSessionsDialogComponent } from './components';
import { CommonQuestionModel, FormDialogData, ResourceQuestion } from './models';
@Component({
  selector: 'app-distribute-common-questions',
  templateUrl: './distribute-common-questions.component.html',
  styleUrls: ['./distribute-common-questions.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DistributeCommonQuestionsComponent
  extends ComponentBase
  implements FormioCustomComponent<any>, OnInit, OnChanges
{
  dir: 'rtl' | 'ltr';

  private _value: any = null;
  @Input()
  public set value(v) {
    this._value = v;
  }
  public get value() {
    return this._value;
  }

  formioEvent?: EventEmitter<FormioEvent>;

  @Input() data: any;
  @Input() customOptions: any;
  @Input() formioComponent: any;

  @Output() valueChange = new EventEmitter();

  selectedQuestion: CommonQuestionModel = null;
  sideOpened = false;
  visitInfoSub = new BehaviorSubject<VisitInfoModel>(null);
  visitInfo$ = this.visitInfoSub.asObservable();

  /**
   *
   */
  constructor(public injector: Injector) {
    super(injector);
    (this.translateService.onLangChange as Observable<LangChangeEvent>)
      .pipe(
        takeUntil(this.destroy$),
        startWith({
          lang: this.translateService.currentLang,
        }),
        map((lang) => (lang.lang == 'ar' ? 'rtl' : 'ltr'))
      )
      .subscribe((res) => {
        this.dir = res;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.data || changes?.value) {
      this._getVisitInfo(this.data);
    }
  }

  ngOnInit(): void {
    this.questionsStore.questionsStore$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.updateValue(res);
      });
  }

  addQuestion(resourceQuestion: ResourceQuestion) {
    const visit = this.visitInfoSub.getValue();
    const _data: CommonQuestionModel = {
      answer: '',
      questionKey: resourceQuestion.key,
      questionText: resourceQuestion.question,
      visitKey: visit.key,
      visitId: visit.id,
      phase: visit?.phase,
      sessions: [],
    };

    const dialog = this.dialog.open<UsersSessionsDialogComponent, FormDialogData>(
      UsersSessionsDialogComponent,
      {
        width: 'min(80%, 700px)',
        panelClass: 'users-dialog',
        direction: this.dir,
        data: {
          visitKey: visit.key,
          question: _data,
          mode: 'add',
        },
      }
    );
    dialog
      .afterClosed()
      .pipe(
        filter(Boolean),
        takeUntil(this.destroy$),
        switchMap((data: CommonQuestionModel) => {
          const payload = removeEmpty(data);
          return this.questionsService.createQuestion(payload);
        })
      )
      .subscribe((res) => {
        this.questionsStore.pushToStore(res);
      });
  }

  closeQuestion() {
    this.selectedQuestion = null;
    this.sideOpened = false;
  }

  editQuestion(question: CommonQuestionModel) {
    const visit = this.visitInfoSub.getValue();
    const dialog = this.dialog.open<UsersSessionsDialogComponent, FormDialogData>(
      UsersSessionsDialogComponent,
      {
        width: 'min(80%, 700px)',
        panelClass: 'users-dialog',
        direction: this.dir,
        data: {
          visitKey: visit.key,
          question: question,
          mode: 'edit',
        },
      }
    );
    dialog
      .afterClosed()
      .pipe(
        filter(Boolean),
        takeUntil(this.destroy$),
        switchMap((data: CommonQuestionModel) => {
          const payload = removeEmpty(data);
          return this.questionsService.updateQuestion(payload);
        })
      )
      .subscribe((res) => {
        this.questionsStore.refreshStore();
        this._showSuccessAlert();
      });
  }

  deleteQuestion(question: CommonQuestionModel) {
    this._confirmDialog({
      confirmText: this.translateService.instant('forms.common.delete'),
      description: this.translateService.instant(
        'forms.common.deleteCommonQuestionMessage'
      ),
    })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((_) => {
          return this.questionsService.deleteQuestion(question?.id);
        }),
        catchError((error) => {
          this._showErrorAlert();
          return throwError(() => error);
        })
      )
      .subscribe((res) => {
        this.questionsStore.removeFromStore(question?.id);
      });
  }

  openQuestion(question: CommonQuestionModel) {
    this.selectedQuestion = question;
    this.sideOpened = true;
  }

  updateValue(payload) {
    this.value = _cloneDeep(payload);
    this.valueChange.emit(payload);
  }

  private _getVisitInfo(value: Record<string, any>) {
    const data = this.data?.requestDetails?.data;
    const key = data?.projectKey || value?.projectKey;
    if (key) {
      this.questionsService
        .getVisitInfo(key)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          this.visitInfoSub.next({
            ...this.visitInfoSub.getValue(),
            key: res.visitKey,
            id: res.id,
            requestId: res.requestId,
            number: res.visitNumber,
            phase: res.phase,
            visitType: res?.visitType,
          });
        });
    }
  }
}
